import {
  FC,
  Children,
  isValidElement,
  useMemo,
  useState,
  useEffect,
  useCallback
} from 'react';
import { useTranslation } from 'react-i18next';
import { Close as CloseIcon } from '@linktivity/link-icons';
import { Link } from '@linktivity/link-ui';
import MenuIcon from '@link/share/assets/icons/menu.svg?react';
import { useIsMobile } from '@link/share/hooks';
import Account from './Account';
import Cart from './Cart';
import { HeaderContext } from './context';
import Currency from './Currency';
import { HeaderProps } from './header.types';
import Locale from './Locale';
import styles from './header.module.css';

// Above this many items a phone gets a menu toggle instead of the inline row.
const MAX_INLINE_ITEMS_ON_MOBILE = 4;

/**
 * The header shell: logo, plus the nav items passed as children, laid out
 * inline or behind a menu toggle on narrow screens.
 *
 * Pass each nav item as a direct child (`Header.Locale`, `Header.Cart`, …) —
 * the mobile menu counts and wraps children one by one, so a fragment would
 * count as a single item.
 */
const HeaderRoot: FC<HeaderProps> = ({ logo, children }) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const items = Children.toArray(children);
  const showMobileMenu = isMobile && items.length > MAX_INLINE_ITEMS_ON_MOBILE;

  useEffect(() => {
    if (!showMobileMenu) setIsMobileMenuOpen(false);
  }, [showMobileMenu]);

  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobileMenu();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen, closeMobileMenu]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isMobileMenuOpen]);

  const contextValue = useMemo(() => ({ closeMobileMenu }), [closeMobileMenu]);

  return (
    <HeaderContext.Provider value={contextValue}>
      <header className={styles.header}>
        <div className={styles.inner}>
          <div className={styles.left}>
            <div className={styles.logo}>
              {logo && (
                <Link to={`/${i18n.language}`} className={styles.logoInner}>
                  <img src={logo} alt="logo" className={styles.logoImage} />
                </Link>
              )}
            </div>
          </div>
          <div className={styles.right}>
            {showMobileMenu ? (
              <button
                className={styles.menuToggle}
                type="button"
                aria-label={
                  isMobileMenuOpen
                    ? t('share.header.closeMenu')
                    : t('share.header.openMenu')
                }
                aria-controls="mobile-menu-panel"
                aria-expanded={isMobileMenuOpen}
                onClick={() => setIsMobileMenuOpen(v => !v)}
              >
                {isMobileMenuOpen ? (
                  <CloseIcon className={styles.navIcon} />
                ) : (
                  <MenuIcon className={styles.navIcon} />
                )}
              </button>
            ) : (
              <div className={styles.nav}>{items}</div>
            )}
          </div>
        </div>
        {showMobileMenu && isMobileMenuOpen && (
          <>
            <button
              type="button"
              className={styles.mobileMenuBackdrop}
              aria-label={t('share.header.closeMenu')}
              onClick={closeMobileMenu}
            />
            <nav id="mobile-menu-panel" className={styles.mobileMenuPanel}>
              {items.map((item, index) => (
                <div
                  key={isValidElement(item) ? item.key : index}
                  className={styles.mobileMenuItem}
                >
                  {item}
                </div>
              ))}
            </nav>
          </>
        )}
      </header>
    </HeaderContext.Provider>
  );
};

const Header = Object.assign(HeaderRoot, { Locale, Currency, Cart, Account });

export default Header;
